import { useMemo } from "react"

import type { MetricCatalogItemResource } from "@/types_api/backend/transport/metric_catalog_item_resource"

import { useMetricCatalogEntriesTransport } from "../routes/useMetricCatalogTransport"

export type AddCatalogItem = MetricCatalogItemResource

interface AddCatalogGroup {
  category: string
  items: AddCatalogItem[]
}

interface UseAddMetricCatalogResult {
  drafts: AddCatalogItem[]
  groups: AddCatalogGroup[]
  error: ReturnType<typeof useMetricCatalogEntriesTransport>["error"]
  hasData: boolean
  isLoading: boolean
  isEmpty: boolean
  retry: ReturnType<typeof useMetricCatalogEntriesTransport>["refetch"]
}

const CATEGORY_ORDER = ["Audio Analysis", "Transcript Analysis", "Traces"]

export function useAddMetricCatalog(search = ""): UseAddMetricCatalogResult {
  const { data, error, isLoading, refetch } = useMetricCatalogEntriesTransport()

  const { drafts, groups } = useMemo(() => {
    const query = search.trim().toLowerCase()
    const entries = (data?.entries ?? []).filter((entry) => {
      if (!query) {
        return true
      }
      return [
        entry.display_name,
        entry.description,
        entry.category ?? "",
        ...entry.tags,
      ].some((value) => value.toLowerCase().includes(query))
    })
    const drafts = entries
      .filter((entry) => entry.status === "inactive")
      .sort((left, right) =>
        left.display_name.localeCompare(right.display_name),
      )

    const grouped = new Map<string, AddCatalogItem[]>()
    for (const entry of entries.filter(
      (candidate) => candidate.status !== "inactive",
    )) {
      const category = entry.category?.trim() || "Other"
      const existing = grouped.get(category)
      if (existing) {
        existing.push(entry)
      } else {
        grouped.set(category, [entry])
      }
    }

    const groups = Array.from(grouped, ([category, items]) => ({
      category,
      items: items.sort((left, right) =>
        left.display_name.localeCompare(right.display_name),
      ),
    })).sort((left, right) => {
      const leftIndex = CATEGORY_ORDER.indexOf(left.category)
      const rightIndex = CATEGORY_ORDER.indexOf(right.category)
      if (leftIndex !== -1 || rightIndex !== -1) {
        if (leftIndex === -1) {
          return 1
        }
        if (rightIndex === -1) {
          return -1
        }
        return leftIndex - rightIndex
      }
      return left.category.localeCompare(right.category)
    })

    return { drafts, groups }
  }, [data, search])

  return {
    drafts,
    groups,
    error,
    hasData: data !== undefined,
    isLoading,
    isEmpty: data !== undefined && drafts.length === 0 && groups.length === 0,
    retry: refetch,
  }
}
